import { useId, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";
import { Textarea } from "@/components/ui/textarea";
import { useDeleteExpiredVms } from "@/hooks/useResourceQueries";
import {
  EXPIRED_DELETE_REASON_MAX_LENGTH,
  EXPIRED_DELETE_REASON_MIN_LENGTH,
  RESTORE_WINDOW_HOURS,
  type DeleteExpiredVmsResult,
  type ExpiredVm,
} from "@/types/admin";
import { ExpiredVmsFailure } from "./ExpiredVmsFailure";
import { ExpiredVmsResult } from "./ExpiredVmsResult";

const DEFAULT_REASON = "Expired VM clean-up";

/** What the dialog shows of each VM: an Expired VMs row, or a row of the Resources list. */
type BulkDeleteVm = Pick<ExpiredVm, "id" | "name" | "tenantName" | "userEmail">;

interface DeleteExpiredVmsDialogProps {
  /** The VMs to delete; null while the dialog is closed. */
  vms: BulkDeleteVm[] | null;
  recordOnly: boolean;
  /**
   * The lapse the list was read with: each VM is re-checked against it before deletion. Omitted on
   * the Resources list, where no lapse filtered the selection.
   */
  expiredDays?: number;
  /** The reason the field opens with; the Expired VMs clean-up wording when omitted. */
  defaultReason?: string;
  /**
   * Called once on close, with the ids that were deleted (empty when nothing ran), so the caller can
   * drop them from its selection. The list itself is already refreshed: the mutation does that when
   * the request answers, whether or not the report is ever closed.
   */
  onClose: (deletedIds: string[]) => void;
}

/**
 * One confirmation for a single row and for a selection, in both modes. It stays open after the
 * request to show the per-row report — every refused row with the backend's own reason — or, when the
 * request itself failed, that failure. It cannot be closed while the request runs.
 */
export function DeleteExpiredVmsDialog({
  vms,
  recordOnly,
  expiredDays,
  defaultReason = DEFAULT_REASON,
  onClose,
}: DeleteExpiredVmsDialogProps) {
  const reasonFieldId = useId();
  const [reason, setReason] = useState(defaultReason);
  const {
    mutate,
    data: result,
    error,
    isPending,
    isError,
    reset,
  } = useDeleteExpiredVms();

  const open = vms !== null;

  // The request-level failure, when there is one: `isError` alone does not narrow `error` to a value.
  const failureMessage = isError && error != null ? error.message : null;

  const count = vms?.length ?? 0;
  const trimmed = reason.trim();
  const reasonTooShort = trimmed.length < EXPIRED_DELETE_REASON_MIN_LENGTH;
  const isReasonValid =
    !reasonTooShort && trimmed.length <= EXPIRED_DELETE_REASON_MAX_LENGTH;

  const handleConfirm = () => {
    if (!vms || !isReasonValid || isPending) return;
    mutate({
      // No lapse key at all when none filtered the selection (the Resources list).
      payload: {
        resourceIds: vms.map((vm) => vm.id),
        reason: trimmed,
        ...(expiredDays != null ? { expiredDays } : {}),
      },
      recordOnly,
    });
  };

  // Reset here, not in an effect after paint: a reopened dialog must not flash the last report.
  const handleClose = () => {
    const deletedIds =
      result?.results
        .filter((row) => row.success)
        .map((row) => row.resourceId) ?? [];
    reset();
    setReason(defaultReason);
    onClose(deletedIds);
  };

  return (
    <AlertDialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isPending) handleClose();
      }}
    >
      <AlertDialogContent
        className="max-w-lg"
        onEscapeKeyDown={(event) => {
          if (isPending) event.preventDefault();
        }}
      >
        {result ? (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {reportTitle(recordOnly, result)}
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody>
              <ExpiredVmsResult
                result={result}
                successHeading={successHeading(recordOnly, result.successCount)}
                successNote={
                  recordOnly
                    ? undefined
                    : `They show as Terminating until the infrastructure confirms, then stay restorable for ${RESTORE_WINDOW_HOURS}h.`
                }
              />
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </AlertDialogFooter>
          </>
        ) : (
          <>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {confirmTitle(isError, recordOnly, count)}
              </AlertDialogTitle>
            </AlertDialogHeader>
            <AlertDialogBody className="space-y-4">
              {/* The request itself failed: no per-row report exists, and the reason must not be left
                  to a toast that disappears while the operator decides what to do next. */}
              {failureMessage != null && (
                <ExpiredVmsFailure message={failureMessage} />
              )}

              {/* One sentence, as in the single delete: the refusals are listed in the report after. */}
              <AlertDialogDescription>
                {recordOnly
                  ? "Only WiCloud's records are removed; nothing is deleted at the infrastructure."
                  : `Each VM is deleted at the infrastructure and can be restored for ${RESTORE_WINDOW_HOURS}h. A VM still in its term must be stopped first.`}
              </AlertDialogDescription>

              <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border">
                {vms?.map((vm) => (
                  <li
                    key={vm.id}
                    className="flex min-w-0 items-baseline justify-between gap-3 px-3 py-2"
                  >
                    <span className="truncate text-sm font-medium">
                      {vm.name}
                    </span>
                    <span className="shrink-0 truncate text-xs text-muted-foreground">
                      {vm.tenantName ?? vm.userEmail ?? "—"}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="space-y-2">
                <Label htmlFor={reasonFieldId}>
                  Reason for deletion{" "}
                  <span className="text-destructive-ink" aria-hidden="true">
                    *
                  </span>
                </Label>
                <Textarea
                  id={reasonFieldId}
                  required
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Why are these VMs being deleted?"
                  maxLength={EXPIRED_DELETE_REASON_MAX_LENGTH}
                  rows={3}
                  disabled={isPending}
                  aria-invalid={reasonTooShort}
                />
                {reasonTooShort && (
                  <p className="text-xs text-muted-foreground">
                    {`At least ${EXPIRED_DELETE_REASON_MIN_LENGTH} characters.`}
                  </p>
                )}
              </div>

              {isPending && (
                <div className="space-y-2 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                  <p className="flex items-start gap-2">
                    <Spinner className="mt-0.5 shrink-0" />
                    <span>
                      Deleting… each VM is checked at the infrastructure first,
                      this can take a few minutes.
                    </span>
                  </p>
                  <p className="flex items-start gap-2">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-ink" />
                    <span>
                      Do not close this page until the report appears.
                    </span>
                  </p>
                </div>
              )}
            </AlertDialogBody>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
              {/* A plain Button: the action would close the dialog, and it must stay open for the report. */}
              <Button
                variant="destructive"
                onClick={handleConfirm}
                disabled={!isReasonValid || isPending}
              >
                {isPending && <Spinner className="mr-2" />}
                {recordOnly
                  ? `Remove ${count} record${count === 1 ? "" : "s"}`
                  : `Delete ${count} VM${count === 1 ? "" : "s"}`}
              </Button>
            </AlertDialogFooter>
          </>
        )}
      </AlertDialogContent>
    </AlertDialog>
  );
}

/** The confirmation's title: a failed attempt says so, instead of asking the same question again. */
function confirmTitle(
  isError: boolean,
  recordOnly: boolean,
  count: number,
): string {
  if (isError) {
    return recordOnly ? "The record removal failed" : "The deletion failed";
  }

  const verb = recordOnly ? "Remove" : "Delete";
  const noun = recordOnly ? "record" : "VM";
  return `${verb} ${count} ${noun}${count === 1 ? "" : "s"}?`;
}

/**
 * The report's title: a batch that refused rows must not read as a clean finish, and a full deletion
 * has only started when the request answers — the infrastructure finishes it afterwards.
 */
function reportTitle(
  recordOnly: boolean,
  result: DeleteExpiredVmsResult,
): string {
  if (result.successCount === 0) {
    return recordOnly ? "No records were removed" : "No VMs were deleted";
  }

  if (result.failureCount > 0) {
    return recordOnly
      ? "Some records could not be removed"
      : "Deletion started, some VMs failed";
  }

  return recordOnly ? "Records removed" : "Deletion started";
}

/** The heading over the rows that went through. */
function successHeading(recordOnly: boolean, count: number): string {
  if (recordOnly) {
    return `${count} record${count === 1 ? "" : "s"} removed`;
  }

  return `Deletion started for ${count} VM${count === 1 ? "" : "s"}`;
}
